import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as S from '../additional-providers-form/additional-providers-form.styles';
import * as CS from '../section.styles';
import { BaseButton } from '../../../base';
import { CreateClaimProvider, CreateClaimProviderType, SupportProvider } from '../../../../api';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';

import { InputField } from '../../../base/form';
import { ProviderAutocomplete } from '../additional-providers-form/provider-autocomplete';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface ProviderFormValues {
    providerLookup?: string;
    providerId?: string;
    organization: string;
    first: string;
    last: string;
    npi: string;
}

const IDENTITY_ERROR_MESSAGE = 'Either Organization or both First and Last name are required';

const ProviderSchema = Yup.object().shape({
    organization: Yup.string().test(
        'organization-or-first-last',
        IDENTITY_ERROR_MESSAGE,
        function (value) {
            const { first, last } = this.parent as ProviderFormValues;
            const organization = (value || '').trim();
            const firstName = (first || '').trim();
            const lastName = (last || '').trim();
            const hasOrganization = !!organization;
            const hasFullName = !!firstName && !!lastName;
            return hasOrganization || hasFullName;
        },
    ),
    first: Yup.string(),
    last: Yup.string(),
    npi: Yup.string().required('NPI is required').length(10, 'NPI must be exactly 10 characters'),
});

const initialValues: ProviderFormValues = {
    providerLookup: '',
    providerId: '',
    organization: '',
    first: '',
    last: '',
    npi: '',
};

interface Props {
    claimEditorStore: ClaimEditorStore;
    serviceIndex: number;
}

export const ServiceLineProvidersForm: React.FunctionComponent<Props> = observer(({ claimEditorStore, serviceIndex }) => {
    const [showRendering, setShowRendering] = useState(false);
    const [showReferring, setShowReferring] = useState(false);
    const [showSupervising, setShowSupervising] = useState(false);
    const [showOrdering, setShowOrdering] = useState(false);

    useEffect(() => {
        const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
        const providers: any[] = svc.providers || [];
        const types = providers.map(p => String(p?.type).toUpperCase());
        setShowRendering(types.includes('RENDERING'));
        setShowReferring(types.includes('REFERRING'));
        setShowSupervising(types.includes('SUPERVISING'));
        setShowOrdering(types.includes('ORDERING'));
    }, [claimEditorStore.services, serviceIndex]);

    return (
        <S.FormContainer>
            <CS.ButtonContainer>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'RENDERING', {
                            first: '',
                            last: '',
                            npi: '',
                            type: CreateClaimProviderType.RENDERING,
                        });
                        setShowRendering(true);
                    }}
                    disabled={showRendering}
                >
                    + Add Rendering
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'REFERRING', {
                            first: '',
                            last: '',
                            npi: '',
                            type: CreateClaimProviderType.REFERRING,
                        });
                        setShowReferring(true);
                    }}
                    disabled={showReferring}
                >
                    + Add Referring
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'SUPERVISING', {
                            first: '',
                            last: '',
                            npi: '',
                            type: CreateClaimProviderType.SUPERVISING,
                        });
                        setShowSupervising(true);
                    }}
                    disabled={showSupervising}
                >
                    + Add Supervising
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'ORDERING', {
                            first: '',
                            last: '',
                            npi: '',
                            type: CreateClaimProviderType.ORDERING,
                        });
                        setShowOrdering(true);
                    }}
                    disabled={showOrdering}
                >
                    + Add Ordering
                </BaseButton>
            </CS.ButtonContainer>

            {showRendering && (
                <ProviderBlock
                    title="Rendering"
                    type={CreateClaimProviderType.RENDERING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'RENDERING'); setShowRendering(false); }}
                />
            )}
            {showReferring && (
                <ProviderBlock
                    title="Referring"
                    type={CreateClaimProviderType.REFERRING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'REFERRING'); setShowReferring(false); }}
                />
            )}
            {showSupervising && (
                <ProviderBlock
                    title="Supervising"
                    type={CreateClaimProviderType.SUPERVISING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'SUPERVISING'); setShowSupervising(false); }}
                />
            )}
            {showOrdering && (
                <ProviderBlock
                    title="Ordering"
                    type={CreateClaimProviderType.ORDERING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'ORDERING'); setShowOrdering(false); }}
                />
            )}
        </S.FormContainer>
    );
});

const ProviderBlock: React.FunctionComponent<{
    title: string;
    type: CreateClaimProviderType;
    serviceIndex: number;
    claimEditorStore: ClaimEditorStore;
    onDelete: () => void;
}> = observer(({ title, type, serviceIndex, claimEditorStore, onDelete }) => {
    const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Normalize enum or string to canonical uppercase string key
    const typeString = React.useMemo(() => {
        const maybeString = typeof type === 'string' ? type : (CreateClaimProviderType as any)[type];
        return String(maybeString).toUpperCase();
    }, [type]);

    const getInitialValues = useCallback((): ProviderFormValues => {
        const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
        const providers: any[] = Array.isArray(svc.providers)
            ? svc.providers
            : svc.providers && typeof svc.providers === 'object'
                ? Object.values(svc.providers)
                : [];
        const existingProvider = providers.find((p: any) => String(p?.type).toUpperCase() === typeString) as
            | CreateClaimProvider
            | undefined;
        
        if (existingProvider) {
            return {
                providerLookup:
                    [existingProvider.first, existingProvider.last].filter(Boolean).join(' ') ||
                    existingProvider.organization ||
                    '',
                providerId: (existingProvider as any).providerId || (existingProvider as any).id || '',
                organization: existingProvider.organization || '',
                first: existingProvider.first || '',
                last: existingProvider.last || '',
                npi: existingProvider.npi || '',
            };
        }
        return initialValues;
    }, [claimEditorStore.services, serviceIndex, title, typeString]);

    const convertToProvider = (values: ProviderFormValues): CreateClaimProvider => {
        return {
            providerId: values.providerId || undefined,
            organization: values.organization || undefined,
            first: values.first || undefined,
            last: values.last || undefined,
            npi: values.npi || undefined,
            type: typeString as any,
        };
    };

    const debouncedSave = useCallback(
        (values: ProviderFormValues) => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }

            saveTimerRef.current = setTimeout(() => {
                const providerData = convertToProvider(values);
                claimEditorStore.updateServiceLineProvider(serviceIndex, typeString as any, providerData);
            }, 500);
        },
        [claimEditorStore, serviceIndex, typeString],
    );


        useEffect(() => {
            return () => {
                if (saveTimerRef.current) {
                    clearTimeout(saveTimerRef.current);
                }
            };
        }, []);

    return (
        <CS.SectionContainer>
            <CS.SectionHeader>
                <CS.SectionTitle>{title}</CS.SectionTitle>
                <BaseButton variant="warning" onClick={onDelete}>✕ Delete {title} Provider</BaseButton>
            </CS.SectionHeader>

            <Formik
                initialValues={getInitialValues()}
                validationSchema={ProviderSchema}
                onSubmit={() => {
                    // No-op, we auto-save
                }}
                enableReinitialize
                validateOnMount={false}
                validateOnChange={true}
                validateOnBlur={false}
            >
                {formik => {
                    // Auto-save whenever values change
                    useEffect(() => {
                        debouncedSave(formik.values);
                    }, [formik.values, debouncedSave]);

                    return (
                        <>
                            <FormErrorBanner
                                submitCount={formik.submitCount}
                                isValid={formik.isValid}
                                errors={formik.errors}
                            />
                            <S.FormGrid>
                                <ProviderAutocomplete
                                    providerType={type}
                                    formik={formik}
                                    onSelect={(p: SupportProvider) => {
                                        const displayName =
                                            [p.first, p.last].filter(Boolean).join(' ') || p.organization || '';
                                        const newValues: ProviderFormValues = {
                                            providerLookup: displayName,
                                            providerId: p.id || '',
                                            organization: p.organization || '',
                                            first: p.first || '',
                                            last: p.last || '',
                                            npi: p.npi || '',
                                        };
                                        formik.setValues(newValues);
                                    }}
                                    claimEditorStore={claimEditorStore}
                                />

                                <S.FormField>
                                <S.Label required={!formik.values.last.trim() || !formik.values.first.trim()}>Organization Name</S.Label>
                                    <Field
                                        name="organization"
                                        placeholder="Organization Name"
                                        component={InputField}
                                    />
                                </S.FormField>

                                <S.FormField>
                                <S.Label required={!formik.values.organization.trim()}>First Name</S.Label>
                                    <Field name="first" placeholder="First Name" component={InputField} />
                                </S.FormField>

                                <S.FormField>
                                <S.Label required={!formik.values.organization.trim()}>Last Name</S.Label>
                                    <Field name="last" placeholder="Last Name" component={InputField} />
                                </S.FormField>

                                <S.FormField>
                                    <S.Label required>NPI</S.Label>
                                    <Field name="npi" placeholder="NPI" component={InputField} maxLength={10 as any} />
                                </S.FormField>
                            </S.FormGrid>
                        </>
                    );
                }}
            </Formik>
        </CS.SectionContainer>
    );
});

export default ServiceLineProvidersForm;

